import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MSG = {
  subscription: ["Welcome to the inner circle.", "Your membership is active. Every analysis and takeaway is now unlocked.", "/news", "Start reading"],
  report: ["Your report is ready.", "Lifetime access has been added to your account.", "/account", "Go to my account"],
  tip: ["Thank you for backing the journalism.", "Your contribution funds field reporting across the continent.", "/", "Back to the brief"],
  sponsorship: ["Your sponsorship is booked.", "Our partnerships desk will contact you within one working day to schedule placement.", "/sponsor", "Back to partnerships"],
  event: ["Your Origin Cup seat is booked.", "A confirmation email is on its way. The venue details follow in the week before your session.", "/events", "Back to The Origin Cup"],
  event_balance: ["Thank you, your payment is complete.", "Your Origin Cup booking is now fully paid. Stripe will email you a receipt.", "/events", "Back to The Origin Cup"],
  agap: ["Welcome to AGAP.", "Your payment is confirmed. A confirmation email is on its way and the AGAP team will contact you within two working days.", "/agap", "Back to AGAP"],
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "checking" });
  const { refreshUser } = useAuth();

  useEffect(() => {
    const sid = params.get("session_id");
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState({ status: "paid", kind: data.kind }); refreshUser(); return; }
        if (data.status === "expired") { setState({ status: "failed" }); return; }
      } catch { /* keep polling */ }
      if (++tries >= 12) return setState({ status: "timeout" });
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshUser]);

  const m = MSG[state.kind] || MSG.tip;
  return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-left" data-testid="payment-success-page">
      {state.status === "checking" && <p className="flex items-center gap-3 font-display text-2xl" data-testid="payment-checking"><Loader2 className="animate-spin" /> Confirming your payment…</p>}
      {state.status === "paid" && (
        <div data-testid="payment-confirmed">
          <CheckCircle2 className="text-[#0A0A0A]" size={40} />
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-6">{m[0]}</h1>
          <p className="text-[#575757] mt-4">{m[1]}</p>
          <Link to={m[2]} className="inline-block mt-8 bg-[#E5332A] text-white px-6 py-3 text-sm">{m[3]}</Link>
        </div>
      )}
      {(state.status === "failed" || state.status === "timeout") && (
        <div data-testid="payment-not-confirmed">
          <XCircle className="text-[#E5332A]" size={40} />
          <h1 className="font-display text-3xl font-bold mt-6">{state.status === "failed" ? "Payment was not completed." : "We're still confirming your payment."}</h1>
          <p className="text-[#575757] mt-4">Check your account in a moment, or contact us if anything looks wrong.</p>
          <Link to="/account" className="inline-block mt-8 border border-[#0A0A0A] px-6 py-3 text-sm">My account</Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto px-5 py-24" data-testid="payment-cancel-page">
      <h1 className="font-display text-4xl font-bold">Checkout cancelled.</h1>
      <p className="text-[#575757] mt-4">No payment was taken. You can pick up where you left off any time.</p>
      <Link to="/pricing" className="inline-block mt-8 bg-[#0A0A0A] text-white px-6 py-3 text-sm">View plans</Link>
    </div>
  );
}
